/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

/** @jsxImportSource @emotion/react */

import React, { useCallback, useEffect, useState } from 'react'

import {
  ActForm,
  billingEngineQueries,
  isAccountLoadedAndCustomerPaid,
  isNewActType,
} from '@builddocs/data-management'
import { LoadableApplicationKeys } from '@builddocs/filestore-api'
import { useToast } from '@builddocs/system'
import { css } from '@emotion/react'
import { Card, CircularProgress, Stack } from '@mui/material'
import { useQuery } from 'react-query'

import {
  AOSRJsonType,
  ListsFieldsType,
  SecondaryActJsonType,
} from '../Types/dataJSON'

import { getRelativeSizePt } from './molecules/basis'
import { H1 } from './molecules/H1'
import { Paragraph } from './molecules/Paragraph'
import { SubDescription } from './molecules/SubDescription'
import { TableForAct } from './molecules/TableForAct'
import { FrontPage } from './organizms/FrontPage'
import { Inline } from './organizms/Inline'
import { SelectSection } from './organizms/SelectSection'
import { TemplateText } from './organizms/TemplateText'
import {
  IActTemplate,
  IFrontPageSection,
  IHeaderActSection,
  IInlineActSection,
  IPageSeparatorActSection,
  ISelectActSection,
  ISubHeaderActSection,
  ITableActSection,
  ITextActSection,
  ITextTemplateActSection,
  RowDataType,
  TableHeaderType,
} from './template.type'

import './_fonts.scss'

export const width = getRelativeSizePt(605)
export const height = getRelativeSizePt(854)

interface OwnProps {
  selectedField?: any
  onToggle: (
    item: (
      | IHeaderActSection
      | IInlineActSection
      | ITextTemplateActSection
      | ITableActSection
      | IFrontPageSection
      | ISelectActSection
      | IPageSeparatorActSection
      | ITextActSection
      | ISubHeaderActSection
    ) & { value: unknown },
  ) => void
  actItems: AOSRJsonType | SecondaryActJsonType
  onChange: (e: string, key: string, id?: string) => void
  getValue: ({
    value,
    sectionTemplate,
    template,
  }: {
    value: string | RowDataType[][]
    sectionTemplate:
      | IHeaderActSection
      | IInlineActSection
      | ITextTemplateActSection
      | ITableActSection
      | IFrontPageSection
      | ISelectActSection
      | IPageSeparatorActSection
      | ITextActSection
      | ISubHeaderActSection
      | TableHeaderType
    template?: IActTemplate
  }) => string | Array<Array<RowDataType>>
  onAddRowClick?: (key: string, newRow: RowDataType[]) => void
  onRemoveRowClick?: (key: string, rowIndex: number) => void
  onGetFields: (fields: ListsFieldsType) => void
  onSelectShowInfoAboutOrg: (keys: string[]) => void
}

type Props = OwnProps

export const ActRenderer = (props: Props) => {
  const {
    selectedField,
    onToggle,
    actItems,
    getValue,
    onChange,
    onAddRowClick,
    onRemoveRowClick,
    onGetFields,
  } = props
  const { data: account } = useQuery(billingEngineQueries.getBillingAccount())
  const showWarning = isAccountLoadedAndCustomerPaid(account)

  const { toast } = useToast()

  const [template, setTemplate] = useState<IActTemplate>()

  const loadTemplate = useCallback(async () => {
    if (!actItems?.act_type) {
      return
    }

    const {
      act_type,
      act_date: { value: date },
    } = actItems

    const postfix =
      (act_type === ActForm.AOSR || act_type === ActForm.AOOK) &&
      isNewActType(date)
        ? 'NEW'
        : ''

    try {
      const temp = await import(
        `./JSONTemplates/${act_type}${postfix}_template.json`
      )

      setTemplate(temp)
    } catch (e) {
      toast.warning('Шаблон не найден')
    }
  }, [actItems.act_type])

  useEffect(() => {
    loadTemplate()
  }, [loadTemplate])

  /** Добавление обязательных полей в запрос для обновления акта */
  useEffect(() => {
    if (!template) {
      return
    }

    const fieldsSelectMaterials = template.content
      ?.filter(item => item.editable && item.dataType === 'MATERIAL')
      .map(item => item.key)

    const fieldsSelectWorks = template.content
      ?.filter(item => item.editable && item.dataType === 'WORK')
      .map(item => item.key)

    const fieldsSelectDocuments = template.content
      .filter(
        section =>
          section.editable &&
          section.dataType === 'DOCUMENTS' &&
          section.documentTypes,
      )
      .map(section => ({
        [section.key]: section.documentTypes as LoadableApplicationKeys[],
      }))

    const fieldsContentOrganizations = template.content
      ?.filter(item => item.editable && item.dataType === 'ORGANIZATION')
      .map(item => item.key)

    const fieldsAssignee = template.content
      .reduce((acc: string[], item) => {
        if ('items' in item && Array.isArray(item.items)) {
          return [
            ...acc,
            ...item.items.reduce(
              (acc: string[], item) =>
                item.editable &&
                (item.dataType === 'ASSIGNEE' || item.dataType === 'SIGN')
                  ? [...acc, item.key]
                  : acc,
              [],
            ),
          ]
        } else if (item.dataType === 'ASSIGNEE' || item.dataType === 'SIGN') {
          return [...acc, item.key]
        } else if (
          item.key === 'signatures_table' &&
          item.dataType === 'EDITABLE_ASSIGNEES_TABLE'
        ) {
          const tableHeaderAssignees = (
            item as ITableActSection
          ).headers.reduce((assigneesKeys, tableRow) => {
            for (const td of tableRow) {
              if (td.dataType === 'ASSIGNEE') {
                return [...assigneesKeys, td.key ?? '']
              }
            }
            return assigneesKeys
          }, [] as Array<string>)

          return [...acc, ...tableHeaderAssignees]
        } else {
          return acc
        }
      }, [])
      .map(key => (key.includes('_sign') ? key.replace('_sign', '') : key))

    const fieldsText = template.content.reduce((acc: string[], item) => {
      if ('items' in item && Array.isArray(item.items)) {
        return [
          ...acc,
          ...item.items.reduce(
            (acc: string[], item) =>
              item.editable &&
              (item.dataType === 'TEXT' ||
                item.dataType === 'SELECT' ||
                item.dataType === 'OTHER')
                ? [...acc, item.key]
                : acc,
            [],
          ),
        ]
      } else {
        return item.editable &&
          (item.dataType === 'TEXT' ||
            item.dataType === 'SELECT' ||
            item.dataType === 'OTHER')
          ? [...acc, item.key]
          : acc
      }
    }, [])

    const fieldsContentText = template.content.reduce<string[]>(
      (acc, section) => {
        if (section.type === 'template_text') {
          const parts = section.text
            .split('**')
            .filter((_part, index) => index % 2)
            .map((part: string) => part.replace('!', ''))

          return [...acc, ...parts]
        }

        return acc
      },
      [],
    )

    const fieldsFooterText = template.content.reduce<string[]>(
      (acc, section) => {
        if (section.type === 'front_page') {
          let parts: string[] = []

          section.footer?.forEach(footerSection => {
            if (footerSection.type === 'template_text') {
              const footerParts = footerSection.text
                .split('**')
                .filter((_part, index) => index % 2)
                .map((part: string) => part.replace('!', ''))

              parts = [...parts, ...footerParts]
            }
          })

          return [...acc, ...parts]
        }

        return acc
      },
      [],
    )

    onGetFields &&
      onGetFields({
        selectedWorks: fieldsSelectWorks,
        selectMaterials: fieldsSelectMaterials,
        selectDocuments: fieldsSelectDocuments,
        textFields: [...fieldsContentText, ...fieldsFooterText, ...fieldsText],
        assigneesFields: fieldsAssignee,
        organizationsFields: fieldsContentOrganizations,
      })
  }, [template])

  const getSection = (
    sectionTemplate:
      | IHeaderActSection
      | IInlineActSection
      | ITextTemplateActSection
      | ITableActSection
      | IFrontPageSection
      | ISelectActSection
      | IPageSeparatorActSection
      | ITextActSection
      | ISubHeaderActSection,
  ) => {
    const item = {
      ...sectionTemplate,
      value: sectionTemplate.key
        ? getValue({
            value: actItems[sectionTemplate.key]?.value,
            sectionTemplate,
          })
        : '',
    }

    switch (sectionTemplate.type) {
      case 'header': {
        const section = sectionTemplate as IHeaderActSection

        return (
          <div
            key={section.text[1]}
            css={css`
              display: flex;
              justify-content: center;
            `}
          >
            <div
              css={css`
                width: ${getRelativeSizePt(360)};
                margin: ${getRelativeSizePt(8)} 0 ${getRelativeSizePt(6)};
              `}
            >
              {section.text.map((item, index) => (
                <H1
                  key={index}
                  css={css`
                    text-align: center;
                    margin: 0;
                  `}
                >
                  {item}
                </H1>
              ))}
            </div>
          </div>
        )
      }
      case 'template_text': {
        return (
          <TemplateText
            key={(sectionTemplate as ITextTemplateActSection).text}
            actItems={actItems}
            section={sectionTemplate as ITextTemplateActSection}
            selectedField={selectedField}
            onChange={onChange}
            onToggle={onToggle}
          />
        )
      }
      case 'inline': {
        return (
          <Inline
            key={(sectionTemplate as IInlineActSection).items
              .map(item => item.key)
              .join()}
            actItems={actItems}
            getValue={getValue}
            section={sectionTemplate as IInlineActSection}
            selectedField={selectedField}
            onToggle={onToggle}
          />
        )
      }
      case 'subheader': {
        return (
          <H1
            key={sectionTemplate.title}
            css={css`
              text-align: ${sectionTemplate.styles?.align};
            `}
          >
            {sectionTemplate.title}
          </H1>
        )
      }
      case 'table': {
        return (
          <TableForAct
            key={sectionTemplate.key}
            actItems={actItems}
            getValue={getValue}
            section={sectionTemplate as ITableActSection}
            selectedField={selectedField}
            onAddRowClick={onAddRowClick}
            onChange={(value, key, id) =>
              onChange(
                value ?? '',
                `${key}.value[${id.split('_')[0]}][${id.split('_')[1]}].text`,
              )
            }
            onRemoveRowClick={onRemoveRowClick}
            onToggle={onToggle}
          />
        )
      }
      case 'front_page': {
        const section = sectionTemplate as IFrontPageSection

        return (
          <FrontPage
            key={section.title}
            actItems={actItems}
            getValue={getValue}
            orientation={template && template.config.orientation}
            section={section}
            selectedField={selectedField}
            onChange={onChange}
            onToggle={onToggle}
          />
        )
      }

      case 'select': {
        const section = sectionTemplate as ISelectActSection

        return (
          <SelectSection
            key={section.options.join()}
            actItems={actItems}
            section={section}
            selectedField={selectedField}
            onToggle={onToggle}
          />
        )
      }

      case 'page_separator': {
        return (
          <div
            css={css`
              border-bottom: 1px solid #ced2d6;
              width: 100%;
              margin: ${getRelativeSizePt(30)} 0 ${getRelativeSizePt(30)} 0;
            `}
          />
        )
      }

      case 'text':
      default: {
        const value = getValue({
          value: actItems[sectionTemplate.key]?.value,
          sectionTemplate,
          template,
        })

        const isEmpty = !value || !value?.length

        return (
          <div
            key={sectionTemplate.key}
            css={css`
              width: ${sectionTemplate.styles?.width};
              align-self: ${sectionTemplate.styles?.align};
            `}
          >
            <H1>{sectionTemplate.title}</H1>

            <Paragraph
              id={sectionTemplate.key}
              isEditable={Boolean(item?.editable)}
              selectedId={selectedField}
              warningMark={
                sectionTemplate.warningMark && isEmpty && showWarning
              }
              onChange={onChange}
              onToggle={() => onToggle(item)}
            >
              {value}
            </Paragraph>

            {sectionTemplate.sub_description && (
              <SubDescription id={sectionTemplate.sub_description}>
                {sectionTemplate.sub_description}
              </SubDescription>
            )}
          </div>
        )
      }
    }
  }

  return (
    <Stack className="Pages">
      {template ? (
        <Card
          raised
          sx={{
            m: 2,
            mt: 1,
            py: 4,
            pl: 3,
            pr: 5,
            width:
              template.config?.orientation === 'portrait'
                ? `${width}`
                : `${height}`,
            minHeight:
              template.config?.orientation === 'portrait'
                ? `${height}`
                : `${width}`,
          }}
        >
          {template.content?.map(getSection)}
        </Card>
      ) : (
        <CircularProgress />
      )}
    </Stack>
  )
}

export default React.memo(ActRenderer)
